import { createAsyncThunk, createSlice, current } from '@reduxjs/toolkit';
import {
    getAllItemsListAPI,
    getItemsWithPricesAPI,
    createItemAPI,
    updateItemAPI,
    deleteItemAPI,
    getItemsByRateCardAPI,
    searchItemsAPI,
    assignItemToRateCardAPI,
    updateRateCardItemAPI,
    removeItemFromRateCardAPI
} from "../../api/items";
import { putItemsOffline, updateItemFieldOffline } from "../../store/offlineDb";

// ==================== Async Thunks ====================

/**
 * Dohvata sve stavke za operatora
 */
export const fetchAllItems = createAsyncThunk(
    "items/fetchAll",
    async (_, { rejectWithValue }) => {
        try {
            const response = await getAllItemsListAPI();
            const items = response.data || [];

            // Izvuci kategorije
            const categories = {};
            items.forEach(item => {
                if (item.category) {
                    categories[item.category.toUpperCase()] = true;
                }
            });

            // Sacuvaj za offline
            await putItemsOffline(items);

            return {
                items,
                categories: Object.keys(categories)
            };
        } catch (error) {
            return rejectWithValue(error.data || error.message);
        }
    }
);

/**
 * Kreira novu stavku
 */
export const addItem = createAsyncThunk(
    "items/add",
    async (payload, { rejectWithValue }) => {
        try {
            const { item, image } = payload;

            // Kreiraj stavku
            const response = await createItemAPI(item);
            const createdItem = response.data;

            // TODO: Upload slike ako postoji
            // if (image && createdItem.id) {
            //     await uploadItemImageAPI(createdItem.id, image);
            // }

            return createdItem;
        } catch (error) {
            return rejectWithValue(error.data || error.message);
        }
    }
);

/**
 * Azurira postojecu stavku
 */
export const updateItem = createAsyncThunk(
    "items/update",
    async (payload, { rejectWithValue }) => {
        try {
            const { item } = payload;

            // Azuriraj stavku
            const response = await updateItemAPI(item.id, item);
            const updatedItem = response.data;

            return updatedItem;
        } catch (error) {
            return rejectWithValue(error.data || error.message);
        }
    }
);

/**
 * Brise stavku
 */
export const deleteItem = createAsyncThunk(
    "items/delete",
    async (payload, { rejectWithValue }) => {
        try {
            await deleteItemAPI(payload.id);
            return payload;
        } catch (error) {
            return rejectWithValue(error.data || error.message);
        }
    }
);

/**
 * Menja ime stavke
 */
export const changeItemName = createAsyncThunk(
    "items/changeName",
    async ({ id, name }, { rejectWithValue }) => {
        try {
            const response = await updateItemAPI(id, { name });
            await updateItemFieldOffline(id, 'name', name);
            return { id, name };
        } catch (error) {
            return rejectWithValue(error.data || error.message);
        }
    }
);

/**
 * Pretraga stavki
 */
export const searchItems = createAsyncThunk(
    "items/search",
    async (searchTerm, { rejectWithValue }) => {
        try {
            const response = await searchItemsAPI(searchTerm);
            return response.data || [];
        } catch (error) {
            return rejectWithValue(error.data || error.message);
        }
    }
);

/**
 * Dohvata stavke po cenovniku (sa cenama)
 */
export const fetchItemsByRateCard = createAsyncThunk(
    "items/fetchByRateCard",
    async (rateCardId, { rejectWithValue }) => {
        try {
            const response = await getItemsByRateCardAPI(rateCardId);
            return response.data || [];
        } catch (error) {
            return rejectWithValue(error.data || error.message);
        }
    }
);

/**
 * Dodaje stavku na cenovnik sa cenom
 */
export const assignItemToRateCard = createAsyncThunk(
    "items/assignToRateCard",
    async ({ itemId, rateCardId, data }, { rejectWithValue }) => {
        try {
            const response = await assignItemToRateCardAPI(itemId, rateCardId, data);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.data || error.message);
        }
    }
);

/**
 * Azurira cenu stavke na cenovniku
 */
export const updateRateCardItem = createAsyncThunk(
    "items/updateRateCardItem",
    async ({ itemId, rateCardId, data }, { rejectWithValue }) => {
        try {
            const response = await updateRateCardItemAPI(itemId, rateCardId, data);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.data || error.message);
        }
    }
);

/**
 * Uklanja stavku sa cenovnika
 */
export const removeItemFromRateCard = createAsyncThunk(
    "items/removeFromRateCard",
    async ({ itemId, rateCardId }, { rejectWithValue }) => {
        try {
            await removeItemFromRateCardAPI(itemId, rateCardId);
            return { itemId, rateCardId };
        } catch (error) {
            return rejectWithValue(error.data || error.message);
        }
    }
);

// ==================== Stavke sa cenama (za StationServices) ====================

/**
 * Dohvata sve stavke sa cenama iz aktivnog cenovnika.
 * Koristi se za StationServices stranicu.
 */
export const fetchAllMergedItems = createAsyncThunk(
    "items/fetchAllMerged",
    async (_, { rejectWithValue }) => {
        try {
            const response = await getItemsWithPricesAPI();
            const items = response.data || [];

            // Izvuci kategorije
            const categories = {};
            items.forEach(item => {
                if (item.category) {
                    categories[item.category.toUpperCase()] = true;
                }
            });

            // Sacuvaj za offline
            await putItemsOffline(items);

            return {
                items,
                categories: Object.keys(categories)
            };
        } catch (error) {
            return rejectWithValue(error.data || error.message);
        }
    }
);

// ==================== Initial State ====================

const initialState = {
    allItemsList: [],
    itemsByRateCard: [],
    searchResults: [],
    allItemsCategories: [],
    loading: false,
    loadingRateCardItems: false,
    error: null
};

// ==================== Slice ====================

export const itemsSlice = createSlice({
    name: 'items',
    initialState,
    reducers: {
        clearItems: (state) => {
            state.allItemsList = [];
            state.searchResults = [];
        },
        clearError: (state) => {
            state.error = null;
        },
        clearItemsByRateCard: (state) => {
            state.itemsByRateCard = [];
        }
    },
    extraReducers: (builder) => {
        builder
            // ==================== Fetch All Items ====================
            .addCase(fetchAllItems.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchAllItems.fulfilled, (state, { payload }) => {
                state.loading = false;
                state.allItemsList = payload.items;
                state.allItemsCategories = payload.categories;
            })
            .addCase(fetchAllItems.rejected, (state, { payload }) => {
                state.loading = false;
                state.error = payload;
            })

            // ==================== Add Item ====================
            .addCase(addItem.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(addItem.fulfilled, (state, { payload }) => {
                state.loading = false;
                if (payload) {
                    state.allItemsList = [...state.allItemsList, payload];
                    // Dodaj kategoriju ako ne postoji
                    if (payload.category && !state.allItemsCategories.includes(payload.category.toUpperCase())) {
                        state.allItemsCategories = [...state.allItemsCategories, payload.category.toUpperCase()];
                    }
                }
            })
            .addCase(addItem.rejected, (state, { payload }) => {
                state.loading = false;
                state.error = payload;
            })

            // ==================== Update Item ====================
            .addCase(updateItem.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateItem.fulfilled, (state, { payload }) => {
                state.loading = false;
                if (payload) {
                    const arr = [...current(state.allItemsList)];
                    const index = arr.findIndex(i => i.id === payload.id);
                    if (index !== -1) {
                        arr[index] = payload;
                        state.allItemsList = arr;
                    }
                }
            })
            .addCase(updateItem.rejected, (state, { payload }) => {
                state.loading = false;
                state.error = payload;
            })

            // ==================== Delete Item ====================
            .addCase(deleteItem.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteItem.fulfilled, (state, { payload }) => {
                state.loading = false;
                const arr = [...current(state.allItemsList)];
                const index = arr.findIndex(i => i.id === payload.id);
                if (index !== -1) {
                    arr.splice(index, 1);
                    state.allItemsList = arr;
                }
            })
            .addCase(deleteItem.rejected, (state, { payload }) => {
                state.loading = false;
                state.error = payload;
            })

            // ==================== Change Item Name ====================
            .addCase(changeItemName.fulfilled, (state, { payload }) => {
                const { id, name } = payload;
                const items = [...current(state.allItemsList)];
                const index = items.findIndex(value => value.id === id);
                if (index !== -1) {
                    items[index] = { ...items[index], name };
                    state.allItemsList = items;
                }
            })

            // ==================== Search Items ====================
            .addCase(searchItems.fulfilled, (state, { payload }) => {
                state.searchResults = payload;
            })

            // ==================== Fetch By Rate Card ====================
            .addCase(fetchItemsByRateCard.pending, (state) => {
                state.loadingRateCardItems = true;
            })
            .addCase(fetchItemsByRateCard.fulfilled, (state, { payload }) => {
                state.itemsByRateCard = payload;
                state.loadingRateCardItems = false;
            })
            .addCase(fetchItemsByRateCard.rejected, (state) => {
                state.loadingRateCardItems = false;
            })

            // ==================== Assign Item To Rate Card ====================
            .addCase(assignItemToRateCard.fulfilled, (state, { payload }) => {
                if (payload) {
                    state.itemsByRateCard = [...state.itemsByRateCard, payload];
                }
            })

            // ==================== Update Rate Card Item ====================
            .addCase(updateRateCardItem.fulfilled, (state, { payload }) => {
                if (payload) {
                    const index = state.itemsByRateCard.findIndex(
                        i => i.itemId === payload.itemId
                    );
                    if (index !== -1) {
                        state.itemsByRateCard[index] = payload;
                    }
                }
            })

            // ==================== Remove Item From Rate Card ====================
            .addCase(removeItemFromRateCard.fulfilled, (state, { payload }) => {
                const { itemId } = payload;
                state.itemsByRateCard = state.itemsByRateCard.filter(
                    i => i.itemId !== itemId
                );
            })

            // ==================== LEGACY: fetchAllMergedItems ====================
            .addCase(fetchAllMergedItems.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchAllMergedItems.fulfilled, (state, { payload }) => {
                state.loading = false;
                if (payload) {
                    state.allItemsList = payload.items;
                    state.allItemsCategories = payload.categories;
                }
            })
            .addCase(fetchAllMergedItems.rejected, (state) => {
                state.loading = false;
            });
    }
});

export const { clearItems, clearError, clearItemsByRateCard } = itemsSlice.actions;
export const itemsReducer = itemsSlice.reducer;
